import type { JsonValue, SerializedError } from './logger.types'

const POLICY_VERSION = 2
const MAX_DEPTH = 8
const MAX_ARRAY_ITEMS = 100
const MAX_OBJECT_KEYS = 100
const MAX_STRING_LENGTH = 8_000
const MAX_STACK_LENGTH = 32_000
const MAX_PAYLOAD_BYTES = 64_000

const SENSITIVE_KEY_RE =
  /password|passwd|passcode|\botp\b|secret|credential|authorization|cookie|api[_-]?key|private[_-]?key|access[_-]?token|accessToken|refresh[_-]?token|refreshToken|session[_-]?(?:id|token)|session(?:Id|Token)/i
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi

export type RedactionStats = {
  redactedValueCount: number
  truncatedValueCount: number
}

export type SanitizedPayload = {
  value: Record<string, unknown>
  stats: RedactionStats
}

function truncate(value: string, max: number, stats: RedactionStats): string {
  if (value.length <= max) return value
  stats.truncatedValueCount += 1
  return `${value.slice(0, max)}…[TRUNCATED:${value.length - max}]`
}

function sanitizeString(value: string, stats: RedactionStats): string {
  return sanitizeText(value, MAX_STRING_LENGTH, stats)
}

function sanitizeText(
  value: string,
  maxLength: number,
  stats: RedactionStats
): string {
  let next = value
  next = next.replace(JWT_RE, () => {
    stats.redactedValueCount += 1
    return '[REDACTED_JWT]'
  })
  next = next.replace(BEARER_RE, () => {
    stats.redactedValueCount += 1
    return 'Bearer [REDACTED]'
  })
  next = next.replace(EMAIL_RE, () => {
    stats.redactedValueCount += 1
    return '[REDACTED_EMAIL]'
  })
  return truncate(next, maxLength, stats)
}

function errorCode(error: Error): string | number | undefined {
  const code = (error as Error & { code?: unknown }).code
  return typeof code === 'string' || typeof code === 'number' ? code : undefined
}

function asSerializedError(
  error: Error,
  seen: WeakSet<object>,
  stats: RedactionStats,
  depth: number
): SerializedError {
  const out: SerializedError = {
    name: sanitizeString(error.name || 'Error', stats),
    message: sanitizeString(error.message || String(error), stats)
  }
  if (error.stack)
    out.stack = sanitizeText(error.stack, MAX_STACK_LENGTH, stats)
  const code = errorCode(error)
  if (code != null) out.code = code

  const cause = (error as Error & { cause?: unknown }).cause
  if (cause !== undefined) {
    out.cause = sanitizeValue(cause, 'cause', seen, stats, depth + 1)
  }

  if (
    typeof AggregateError !== 'undefined' &&
    error instanceof AggregateError
  ) {
    out.errors = Array.from(error.errors)
      .slice(0, MAX_ARRAY_ITEMS)
      .map(item => {
        const sanitized = sanitizeValue(item, 'error', seen, stats, depth + 1)
        return sanitized instanceof Object && 'name' in sanitized
          ? (sanitized as SerializedError)
          : sanitized
      })
  }
  return out
}

function sanitizeValue(
  input: unknown,
  key: string,
  seen: WeakSet<object>,
  stats: RedactionStats,
  depth: number
): JsonValue {
  if (SENSITIVE_KEY_RE.test(key)) {
    stats.redactedValueCount += 1
    return '[REDACTED]'
  }
  if (input === null || input === undefined)
    return input === null ? null : '[undefined]'
  if (typeof input === 'string') {
    return key.toLowerCase().includes('url')
      ? sanitizeUrl(input, stats)
      : sanitizeString(input, stats)
  }
  if (typeof input === 'number')
    return Number.isFinite(input) ? input : String(input)
  if (typeof input === 'boolean') return input
  if (typeof input === 'bigint') return `${input.toString()}n`
  if (typeof input === 'symbol') return String(input)
  if (typeof input === 'function')
    return `[Function:${input.name || 'anonymous'}]`
  if (depth >= MAX_DEPTH) {
    stats.truncatedValueCount += 1
    return '[MAX_DEPTH]'
  }
  if (input instanceof Error)
    return asSerializedError(input, seen, stats, depth)
  if (input instanceof Date)
    return Number.isNaN(input.getTime()) ? 'Invalid Date' : input.toISOString()
  if (input instanceof URL) return sanitizeUrl(input.toString(), stats)
  if (typeof Element !== 'undefined' && input instanceof Element) {
    return `[Element:${input.tagName.toLowerCase()}]`
  }
  if (typeof input !== 'object') return sanitizeString(String(input), stats)
  if (seen.has(input)) return '[Circular]'
  seen.add(input)

  if (Array.isArray(input)) {
    if (input.length > MAX_ARRAY_ITEMS) stats.truncatedValueCount += 1
    return input
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item, index) =>
        sanitizeValue(item, String(index), seen, stats, depth + 1)
      )
  }
  if (input instanceof Map) {
    return sanitizeValue(Object.fromEntries(input), key, seen, stats, depth + 1)
  }
  if (input instanceof Set) {
    return sanitizeValue(Array.from(input), key, seen, stats, depth + 1)
  }

  const output: Record<string, JsonValue> = {}
  const entries = Object.entries(input as Record<string, unknown>)
  if (entries.length > MAX_OBJECT_KEYS) stats.truncatedValueCount += 1
  for (const [childKey, childValue] of entries.slice(0, MAX_OBJECT_KEYS)) {
    if (childValue === undefined) continue
    try {
      output[childKey] = sanitizeValue(
        childValue,
        childKey,
        seen,
        stats,
        depth + 1
      )
    } catch {
      output[childKey] = '[SERIALIZATION_ERROR]'
    }
  }
  return output
}

export function sanitizeUrl(value: string, stats?: RedactionStats): string {
  const localStats = stats ?? { redactedValueCount: 0, truncatedValueCount: 0 }
  try {
    const base =
      typeof window === 'undefined'
        ? 'https://logger.invalid'
        : window.location.origin
    const url = new URL(value, base)
    const wasRelative = !/^[a-z][a-z\d+.-]*:/i.test(value)
    if (url.username || url.password || url.search || url.hash) {
      localStats.redactedValueCount += 1
    }
    url.username = ''
    url.password = ''
    url.search = ''
    url.hash = ''
    const sanitized = wasRelative
      ? `${url.pathname}`
      : `${url.origin}${url.pathname}`
    return truncate(sanitized, MAX_STRING_LENGTH, localStats)
  } catch {
    return sanitizeString(value.split(/[?#]/, 1)[0], localStats)
  }
}

export function sanitizePayload(
  payload: Record<string, unknown>
): SanitizedPayload {
  const stats: RedactionStats = {
    redactedValueCount: 0,
    truncatedValueCount: 0
  }
  const value = sanitizeValue(payload, 'payload', new WeakSet(), stats, 0)
  const record =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : { value }
  const encoded = JSON.stringify(record)
  if (encoded.length <= MAX_PAYLOAD_BYTES) return { value: record, stats }
  stats.truncatedValueCount += 1
  return {
    value: {
      _truncated: true,
      _originalBytesApprox: encoded.length,
      preview: truncate(encoded, MAX_PAYLOAD_BYTES, stats)
    },
    stats
  }
}

export function serializeError(error: unknown): SerializedError {
  const stats: RedactionStats = {
    redactedValueCount: 0,
    truncatedValueCount: 0
  }
  if (error instanceof Error) {
    return asSerializedError(error, new WeakSet(), stats, 0)
  }
  return {
    name: 'NonErrorThrown',
    message: sanitizeString(String(error), stats),
    cause: sanitizeValue(error, 'cause', new WeakSet(), stats, 0)
  }
}

export const REDACTION_POLICY_VERSION = POLICY_VERSION
